import React from "react";
import { connect } from "react-redux";
import { isEqual, isEmpty } from "lodash";
import { bindActionCreators } from "redux";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash";
import format from "date-fns/format";
import { DatesRangeInput } from "semantic-ui-calendar-react";
import { Header, Button, Dropdown, Icon } from "semantic-ui-react";

import { loadInvoiceGridPage, changeInvoiceGridPage, changeInvoiceGridState } from "./actions";
import GridPageSizePicker from "../../common/components/grid-page-size-picker";
import { DateFormat } from "utilities/dist/invoice/constants";
import { handleBottlePosJsonExport, handleExportInvoice } from "../helpers";
import { CompanyFeatures } from "utilities/dist/company/constants";
import { isCompanyFeatureActive } from "utilities/dist/company/helpers";
import CustomerContainerPicker from "../../customer/containers/picker";

const NoInvoicesOverlay = () => {
    return (
        <div className="tm-20">
            <Header>You have not created any invoice yet.</Header>
        </div>
    );
};

class DataGrid extends React.Component {
    state = {
        dateRange: "",
    };

    grid = null;
    gridDataSource = {
        getRows: params => {
            this.props.loadInvoiceGridPage(this.props, params);
        },
    };

    handleDateRangeChange = (e, { value }) => {
        const filterModel = { ...this.props.filterModel };
        const [start, end] = value?.split(" - ");

        if (start && this.grid) {
            filterModel.invoiced_at = {
                type: "inRange",
                filterType: "date",
                dateTo: format(new Date(end || start), "yyyy-MM-dd"),
                dateFrom: format(new Date(start), "yyyy-MM-dd"),
            };
        } else if (filterModel.invoiced_at) {
            delete filterModel["invoiced_at"];
        }

        this.props.changeInvoiceGridState({ filterModel });
        this.setState({ dateRange: value });
    };

    componentWillUnmount() {
        this.grid = null;
    }

    onGridReady = grid => {
        this.grid = grid;

        setTimeout(() => this.grid && this.grid.api.sizeColumnsToFit(), 300);

        this.grid.api.setDatasource(this.gridDataSource);
        this.grid.columnApi.applyColumnState({ state: this.props.sortModel, defaultState: {} });
        this.grid.api.setFilterModel(this.props.filterModel);

        this.grid.api.addEventListener("sortChanged", () => {
            const colStates = this.grid.columnApi.getColumnState();
            const sortModel = colStates
                .filter(colState => colState.sort)
                .map(colState => ({
                    colId: colState.colId,
                    sort: colState.sort,
                }));

            this.props.changeInvoiceGridState({
                sortModel,
            });
        });

        this.grid.api.addEventListener("filterChanged", () => {
            this.props.changeInvoiceGridState({
                filterModel: this.grid.api.getFilterModel(),
            });
        });
    };

    reloadGridData = () => {
        this.grid && this.grid.api.refreshInfiniteCache();
    };

    changePage = () => {
        if (this.grid) {
            const page = this.grid.api.paginationGetCurrentPage() + 1;

            if (page === this.props.currentPage) return;

            this.props.changeInvoiceGridPage(page);
        }
    };

    componentDidUpdate(prevProps) {
        if (!this.grid) return;

        if (prevProps.isLoading && !this.props.isLoading) {
            this.grid.api.hideOverlay();
        }

        if (!prevProps.isLoading && this.props.isLoading) {
            this.grid.api.showLoadingOverlay();
        }

        if (!this.props.isLoading && this.props.invoices.length < 1) {
            this.grid.api.showNoRowsOverlay();
        }

        if (!isEqual(prevProps.filterModel, this.props.filterModel)) {
            this.grid.api.setFilterModel(this.props.filterModel);
        }

        if (!isEqual(prevProps.customerId, this.props.customerId)) {
            this.grid.api.refreshInfiniteCache();
        }
    }

    handleCustomerChange = customer => {
        const customerId = get(customer, "id", null);
        this.props.changeInvoiceGridState({ customerId });
    };

    // This is called on every render of the grid so needs to be fast.
    // forEachNode is faster to iterate over the rows than getRenderedNodes
    // Any error happening here can crash the grid so let's catch errors here
    getFooterData = () => {
        try {
            if (!this.grid?.api) return [{ total: 0, items: [] }];
            let total = 0;
            const items = [];
            this.grid.api.forEachNode(node => {
                total += node.data?.total || 0;
                items.push(...(node.data?.items || []));
            });
            return [{ total, items }];
        } catch (e) {
            return [{ total: 0, items: [] }];
        }
    };

    render() {
        const hasFilterApplied = !isEmpty(this.props.filterModel) || !!this.props.customerId;
        const shouldShowExportBottlePos = isCompanyFeatureActive(
            this.props.company,
            CompanyFeatures.BOTTLE_POS
        );

        const isCustomerInvoiceNumberingActive = isCompanyFeatureActive(
            this.props.company,
            CompanyFeatures.CUSTOMER_INVOICE_NUMBERING
        );

        let columnDefs = [...this.props.columnDefs];
        if (!isCustomerInvoiceNumberingActive) {
            columnDefs = columnDefs.map(def => {
                if (def.field === "number") {
                    def.type = "numericColumn";
                    def.filter = "agNumberColumnFilter";
                }

                return def;
            });
        }

        const getInvoicesFromGrid = () => {
            const invoices = [];
            this.grid.api.forEachNode(node => invoices.push(node.data));
            return invoices;
        };

        const options = [
            {
                key: 1,
                text: "Summary(.csv)",
                onClick: () => {
                    handleExportInvoice(getInvoicesFromGrid(), this.props.company, "summary");
                },
                "data-cy": "export_summary",
            },
            {
                key: 2,
                text: "Detailed(.csv)",
                onClick: () =>
                    handleExportInvoice(getInvoicesFromGrid(), this.props.company, "detailed"),
                "data-cy": "export_detailed",
            },
        ];
        if (shouldShowExportBottlePos) {
            options.push({
                key: 3,
                text: "Bottle POS(.json)",
                onClick: () => handleBottlePosJsonExport(this.props.filterModel),
            });
        }

        return (
            <>
                <div className="clear bm-10">
                    <CustomerContainerPicker
                        dropdownProps={{
                            size: "mini",
                            fluid: false,
                            clearable: true,
                            placeholder: "Filter by Customer",
                        }}
                        selectedCustomerId={this.props.customerId}
                        onSelect={this.handleCustomerChange}
                    />

                    <GridPageSizePicker
                        button
                        compact
                        pointing="top"
                        pageSize={this.props.pageSize}
                        className="right floated basic"
                        text={`${this.props.pageSize}/page`}
                        onChange={this.props.changeInvoiceGridState}
                    />

                    {hasFilterApplied && (
                        <Button
                            size="mini"
                            icon="filter"
                            floated="right"
                            content="Clear Filters"
                            onClick={() => {
                                this.props.changeInvoiceGridState({ customerId: null });
                                this.grid.api.setFilterModel({});
                            }}
                        />
                    )}

                    <Dropdown
                        options={options}
                        button
                        className="ui mini right floated button"
                        placeholder="Export"
                        floated="right"
                        content="Export"
                        trigger={
                            <span>
                                <Icon name="download" />
                                Export
                            </span>
                        }
                        data-cy="export_button"
                    />

                    <DatesRangeInput
                        size="mini"
                        className="fr"
                        clearable={true}
                        animation={null}
                        maxDate={new Date()}
                        placeholder="From - To"
                        value={this.state.dateRange}
                        dateFormat={DateFormat.toUpperCase()}
                        onChange={this.handleDateRangeChange}
                    />
                </div>
                <div className="ag-theme-balham bm-10 clear">
                    <AgGridReact
                        pagination
                        rowHeight={56}
                        paginationPageSize={this.props.pageSize}
                        rowSelection="single"
                        domLayout="autoHeight"
                        rowModelType="infinite"
                        onGridReady={this.onGridReady}
                        columnDefs={columnDefs}
                        cacheBlockSize={this.props.pageSize}
                        onPaginationChanged={this.changePage}
                        noRowsOverlayComponent={NoInvoicesOverlay}
                        defaultColDef={{
                            resizable: true,
                            sortable: true,
                            floatingFilter: true,
                            autoHeight: true,
                        }}
                        pinnedBottomRowData={this.getFooterData(this.grid)}
                    />
                </div>
            </>
        );
    }
}

const mapStateToProps = ({ invoiceGrid, auth }) => ({
    invoices: invoiceGrid.invoices,
    pageSize: invoiceGrid.pageSize,
    isLoading: invoiceGrid.isLoading,
    sortModel: invoiceGrid.sortModel,
    columnDefs: invoiceGrid.columnDefs,
    filterModel: invoiceGrid.filterModel,
    currentPage: invoiceGrid.currentPage,
    customerId: invoiceGrid.customerId,
    company: auth.user.company,
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            loadInvoiceGridPage,
            changeInvoiceGridState,
            changeInvoiceGridPage: page => dispatch(changeInvoiceGridPage(page)),
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(DataGrid);
